import React from 'react'
import { BaseContentfulEntry } from '@components/renderer/types'
import { useNinetailedPersonalizationService } from '@lib/hooks'
import { logError, LogErrorMetrics } from '@lib/tracking/felog'
import { transformToString } from '@lib/utils/error'
import { ComponentTracker } from './analytics/component-tracker'
import { parseExperiences } from './experience-parser'
import { NinetailedLibraryRenderer } from './ninetailed-library-renderer'

export interface NinetailedRendererProps {
  structure: BaseContentfulEntry
  renderer: (structure: BaseContentfulEntry) => JSX.Element | null
}

export const NinetailedRenderer: React.FC<NinetailedRendererProps> = ({ structure, renderer }) => {
  const ninetailedPersonalizationService = useNinetailedPersonalizationService()
  const parsedExperiences = parseExperiences(structure)
  const useNinetailedLibraryRenderer = ninetailedPersonalizationService.shouldUseLibraryRenderer()

  try {
    if (useNinetailedLibraryRenderer) {
      return (
        <NinetailedLibraryRenderer structure={structure} renderer={renderer} parsedExperiences={parsedExperiences} />
      )
    }

    const { variant, index } = ninetailedPersonalizationService.resolveVariant(structure, parsedExperiences)
    return (
      <>
        <ComponentTracker variant={variant.sys} variantIndex={index} />
        {renderer(variant)}
      </>
    )
  } catch (error) {
    console.error('Error rendering ninetailed renderer', error)
    logError('Error rendering ninetailed renderer', LogErrorMetrics.Ninetailed, {
      error: transformToString(error as Error),
    })

    return (
      <>
        <ComponentTracker variant={structure.sys} variantIndex={0} />
        {renderer(structure)}
      </>
    )
  }
}
